/**
 * Outcome Adjustments — Feedback Loop Activation (I-A-15)
 *
 * Bounded outcome-based priority adjustments derived from
 * I-A-14 outcome patterns. Closes the Pensieve feedback loop
 * by allowing real-world resolution patterns to safely influence
 * recommendation and signal ranking.
 *
 * Rules:
 *  - Outcome adjustment capped to ±5
 *  - Combined (learning + pattern + outcome) capped to ±25
 *  - Every adjustment is explainable
 *  - No cross-org blending, no user scoring
 *  - Insufficient data → zero adjustment
 */

import type { OutcomePattern } from './outcomePatterns';
import {
  MAX_COMBINED_ADJUSTMENT,
  MIN_COMBINED_ADJUSTMENT,
} from './learningWeights';

// ── Types ──────────────────────────────────────────────

export interface OutcomeAdjustment {
  adjustment: number;   // bounded [-5, +5]
  reasons: string[];    // human-readable explanations
}

export interface RecommendationWithOutcomes {
  base_priority: number;
  learning_adjustment: number;
  pattern_adjustment: number;
  outcome_adjustment: number;
  final_priority: number;
  learning_reasons: string[];
  pattern_reasons: string[];
  outcome_reasons: string[];
}

// ── Constants ──────────────────────────────────────────

const MAX_OUTCOME_ADJUSTMENT = 5;
const MIN_OUTCOME_ADJUSTMENT = -5;

// IV-D-22 R74 FIX-2: MAX/MIN_COMBINED_ADJUSTMENT are now a single source of
// truth in learningWeights.ts (re-exported here to preserve the existing
// import surface for external consumers). Previously the same literals were
// hard-coded in three files; a future bump to ±30 would only take effect in
// one and silently diverge priority scores across composer paths.
export { MAX_COMBINED_ADJUSTMENT, MIN_COMBINED_ADJUSTMENT };

// ── Core computation ───────────────────────────────────

/**
 * Compute a bounded outcome adjustment for a given signal type.
 * Priority order: persistence > retrigger > resolved_after_ack > playbook > self_resolved.
 * Final result clamped to ±5.
 */
export function computeOutcomeAdjustment(
  signalType: string,
  outcomes: OutcomePattern[],
): OutcomeAdjustment {
  const p = outcomes.find((o) => o.signal_type === signalType);

  if (!p || p.total_signals < 2) {
    return { adjustment: 0, reasons: [] };
  }

  let adjustment = 0;
  const reasons: string[] = [];

  // Priority order — each rule evaluated, then clamped at end
  if (p.persistence_rate > 0.5) {
    adjustment += 5;
    reasons.push('Tends to persist if unaddressed');
  }

  if (p.retrigger_rate > 0.4) {
    adjustment += 5;
    reasons.push('Frequently recurs — may need attention');
  }

  if (p.resolved_after_ack_rate > 0.6) {
    adjustment += 5;
    reasons.push('Often resolves after acknowledgment');
  }

  if (p.playbook_usage_rate > 0.3 && p.resolved_after_ack_rate > 0.5) {
    adjustment += 3;
    reasons.push('Playbook-driven resolution observed');
  }

  if (p.self_resolved_rate > 0.5) {
    adjustment -= 5;
    reasons.push('Frequently self-resolves — lower urgency');
  }

  // Hard clamp to ±5
  adjustment = Math.max(MIN_OUTCOME_ADJUSTMENT, Math.min(MAX_OUTCOME_ADJUSTMENT, adjustment));

  return { adjustment, reasons };
}

// ── Recommendation helper ──────────────────────────────

/**
 * Apply outcome adjustments to recommendations that already have
 * learning + pattern weights. Combined cap raised to ±25.
 */
export function applyOutcomeAdjustments<
  T extends {
    type: string;
    base_priority: number;
    learning_adjustment: number;
    pattern_adjustment: number;
    final_priority: number;
    learning_reasons: string[];
    pattern_reasons: string[];
  },
>(
  recs: T[],
  outcomes: OutcomePattern[],
  enabled: boolean,
): (T & RecommendationWithOutcomes)[] {
  return recs.map((rec) => {
    if (!enabled || outcomes.length === 0) {
      return {
        ...rec,
        outcome_adjustment: 0,
        outcome_reasons: [],
      };
    }

    const { adjustment, reasons } = computeOutcomeAdjustment(rec.type, outcomes);

    // Combined cap: learning + pattern + outcome ≤ ±25
    let combinedAdj = rec.learning_adjustment + rec.pattern_adjustment + adjustment;
    combinedAdj = Math.max(MIN_COMBINED_ADJUSTMENT, Math.min(MAX_COMBINED_ADJUSTMENT, combinedAdj));

    // Derive effective outcome adjustment after combined cap
    const effectiveOutcomeAdj = combinedAdj - rec.learning_adjustment - rec.pattern_adjustment;

    return {
      ...rec,
      outcome_adjustment: effectiveOutcomeAdj,
      outcome_reasons: effectiveOutcomeAdj !== 0 ? reasons : [],
      final_priority: rec.base_priority + combinedAdj,
    };
  });
}
